import type {
  BaseMetrics,
  CollectorState,
  Detection,
  EonStatsConfig,
  GameSessionStats,
  Navigator,
} from "../types/eonStats";
import type { StatsMeta } from "../types/stats";
import { flattenObject } from "../utils/object";
import {
  getBrowserId,
  getNewSessionId,
  measureServerTimeDiff,
} from "../utils/eonTracking";

const DEFAULT_CONFIG: Required<Omit<EonStatsConfig, "eonServerEndpoint">> = {
  timeThreshold: 1000, // 1 second for key sequence detection
  maxDetections: 1, // Maximum error detections to keep
  statsThrottle: 5000, // Minimum time between stats posts (5 seconds)
};

const DEFAULT_STATE: CollectorState = {
  sessionStartTime: "",
  loggedinUser: "",
  userId: "",
  gameName: "",
  gameId: "",
  gameSessionId: "",
  streamURL: "",
  tenantName: "",
  deviceType: "",
  codec: "",
  lastStatsPostedTime: -1,
  lastKey: null,
  lastKeyTime: 0,
  detections: [],
};

class EonStatsCollectorService {
  private config: Required<EonStatsConfig>;
  private state: CollectorState;
  private keydownHandler: ((event: KeyboardEvent) => void) | null = null;
  public initialized = false;

  constructor() {
    this.config = {
      ...DEFAULT_CONFIG,
      eonServerEndpoint: "",
    };

    this.state = DEFAULT_STATE;
  }

  /**
   * Initialize the stats collector
   */
  public initialize(config: EonStatsConfig, statsMeta: StatsMeta): void {
    if (this.initialized) return;

    this.config = {
      ...DEFAULT_CONFIG,
      ...config,
    };

    this.state.loggedinUser = statsMeta.username;
    this.state.userId = statsMeta.userId;
    this.state.gameName = statsMeta.gameName;
    this.state.gameId = statsMeta.gameId;
    this.state.gameSessionId = statsMeta.gameSessionId;
    this.state.streamURL = statsMeta.streamURL ?? "";
    this.state.tenantName = statsMeta.tenantName;
    this.state.deviceType = statsMeta.deviceType;
    this.state.codec = statsMeta.codec ?? "";

    // Setup keyboard listener for error detection
    this.setupKeyboardListener();

    this.initialized = true;
    console.log("[stats collector] - initialized, state: ", this.state);
  }

  /**
   * Cleanup resources
   */
  public cleanup(): void {
    this.removeKeyboardListener();
    console.log("[stats collector] - cleaned up");
    this.initialized = false;
  }

  /**
   * Process game launch event
   */
  public async processGameLaunched(): Promise<void> {
    console.log(
      `[stats collector] - game launched, user: ${this.state.loggedinUser} (${this.state.userId}), game: ${this.state.gameName} (${this.state.gameId}), session: ${this.state.gameSessionId}, tenant: ${this.state.tenantName}, device: ${this.state.deviceType}, codec: ${this.state.codec}, streamurl: ${this.state.streamURL}`,
    );

    try {
      // Update state
      this.state.sessionStartTime = await getNewSessionId(
        this.config.eonServerEndpoint,
      );

      // Send game launch event to server
      await this.sendGameLaunchedEvent();
    } catch (error) {
      console.error(
        `[stats collector] - error processing game launched event: `,
        error,
      );
    }
  }

  /**
   * Process game exit event
   */
  public processGameExited(): void {
    if (!this.initialized) return;
    console.log("[stats collector] - game exited");
    this.state = { ...DEFAULT_STATE };
  }

  /**
   * Send game launched event to server
   */
  private async sendGameLaunchedEvent(): Promise<void> {
    const browserId = await getBrowserId(this.config.eonServerEndpoint);

    const params = new URLSearchParams({
      userId: `${this.state.loggedinUser}-${browserId}`,
      userid: this.state.userId,
      username: this.state.loggedinUser,
      gamename: this.state.gameName,
      gameid: this.state.gameId,
      gamesessionid: this.state.gameSessionId,
      tenantname: this.state.tenantName,
      devicetype: this.state.deviceType,
      codec: this.state.codec,
      sessionStTime: this.state.sessionStartTime,
      streamurl: this.state.streamURL,
      clienttime: this.getFormattedTimestamp(),
      clienttimeInGMT: this.getGMTFormattedTimestamp(),
    });

    const response = await fetch(
      `${this.config.eonServerEndpoint}/eventlistener.jsp?${params.toString()}`,
      {
        method: "GET",
        headers: {
          "Cache-Control": "no-cache",
        },
      },
    );

    console.log(
      "[stats collector] - game launched event sent, response:",
      response,
    );

    if (!response.ok) {
      throw new Error(
        `Failed to send game launched event, status: ${response.status}`,
      );
    }
  }

  /**
   * Collect and send metrics to EON server
   */
  public async collectAndSendMetrics(
    gameSessionStats: GameSessionStats,
  ): Promise<void> {
    const currentTime = Date.now();
    if (
      currentTime - this.state.lastStatsPostedTime <
      this.config.statsThrottle
    ) {
      return;
    }

    this.state.lastStatsPostedTime = currentTime;

    try {
      // Ensure session ID exists
      if (!this.state.sessionStartTime) {
        this.state.sessionStartTime = await getNewSessionId(
          this.config.eonServerEndpoint,
        );
      }

      const baseMetrics = await this.collectBaseMetrics();

      if (gameSessionStats.deviceInfo && this.state.codec) {
        const { spec } = gameSessionStats.deviceInfo;
        // `hardware` is absent when nothing in it could be read.
        spec.hardware = { ...spec.hardware, codec: this.state.codec };
      }

      // Append game details and errors to stats (nested)
      const modifiedStats = this.appendGameDetailsAndErrors(gameSessionStats);

      // Flatten the modified stats
      const flattenedStats = flattenObject(modifiedStats);

      // Create extraJson as stringified JSON
      const extraJson = JSON.stringify(modifiedStats);

      const payload = {
        ...baseMetrics, // Base metrics
        ...flattenedStats, // All flattened WebRTC stats
        extraJson: extraJson, // Stringified nested JSON
      };

      // Send to server
      await this.sendMetricsToServer(payload);
    } catch (error) {
      console.error(
        "[stats collector] - [collectAndSendMetrics] - error collecting/sending:",
        error,
      );
    }
  }

  /**
   * Collect base network and browser metrics
   */
  private async collectBaseMetrics(): Promise<BaseMetrics> {
    const connection =
      (navigator as Navigator).connection ||
      (navigator as Navigator).mozConnection ||
      (navigator as Navigator).webkitConnection;

    const browserId = await getBrowserId(this.config.eonServerEndpoint);
    const manualRtt = await measureServerTimeDiff(
      this.config.eonServerEndpoint,
    );

    return {
      timestamp: new Date().toISOString(),
      browserId: browserId,
      loggedinuser: this.state.loggedinUser,
      browser: this.getBrowserName(),
      networkType: connection?.effectiveType || "Unknown",
      downlinkSpeed: connection?.downlink || "Unknown",
      rtt: connection?.rtt || "Unknown",
      manualRtt: manualRtt,
      sessionId: this.state.sessionStartTime,
      gamesessionid: this.state.gameSessionId,
    };
  }

  /**
   * Append game details and error reports to game session stats
   */
  private appendGameDetailsAndErrors(gameSessionStats: GameSessionStats) {
    const extraParams: Record<string, any> = {
      gamedetails: { gamename: this.state.gameName, gameid: this.state.gameId },
    };

    const latest = this.state.detections[this.state.detections.length - 1];

    extraParams["errorReported"] = {
      value: this.state.detections.length,
      timestamp: latest?.timestamp || "N/A",
    };

    // Check if reports section exists
    if ("reports" in gameSessionStats.rtcStats) {
      const modifiedStats = { ...gameSessionStats };
      modifiedStats.rtcStats["reports"] = {
        ...extraParams,
        ...gameSessionStats.rtcStats.reports,
      };

      // Clear detections after use
      this.state.detections = [];

      return modifiedStats;
    }

    // If no reports section, return as is
    console.warn(
      "[stats collector] - no reports section found in game session stats",
    );
    this.state.detections = [];
    return gameSessionStats;
  }

  /**
   * Send metrics to server
   */
  private async sendMetricsToServer(
    metrics: Record<string, any>,
  ): Promise<void> {
    try {
      const response = await fetch(
        `${this.config.eonServerEndpoint}/networkMetrics.jsp`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams(metrics as Record<string, string>),
        },
      );

      if (!response.ok) {
        throw new Error(`Server responded with status: ${response.status}`);
      }

      await response.text();
    } catch (error) {
      console.error("[stats collector] - error sending:", error);
      throw error;
    }
  }

  /**
   * Setup keyboard listener for error detection
   */
  private setupKeyboardListener(): void {
    this.keydownHandler = (event: KeyboardEvent) => {
      this.handleKeyPress(event.key);
    };
    document.addEventListener("keydown", this.keydownHandler);
  }

  /**
   * Remove keyboard listener
   */
  private removeKeyboardListener(): void {
    if (this.keydownHandler) {
      document.removeEventListener("keydown", this.keydownHandler);
      this.keydownHandler = null;
    }
  }

  /**
   * Handle key press for error detection (9+0 sequence)
   */
  private handleKeyPress(key: string): void {
    // Only track '9' and '0' keys
    if (key !== "9" && key !== "0") {
      this.state.lastKey = key;
      return;
    }

    const currentTime = Date.now();

    // Check if keys pressed within threshold time
    if (
      this.state.lastKey &&
      currentTime - this.state.lastKeyTime <= this.config.timeThreshold
    ) {
      const sequence = this.state.lastKey + key;

      // Detect "90" sequence
      if (sequence === "90") {
        this.addDetection(currentTime);
      }
    }

    // Update last key state
    this.state.lastKey = key;
    this.state.lastKeyTime = currentTime;
  }

  /**
   * Add error detection
   */
  private addDetection(timestamp: number): void {
    const detection: Detection = {
      sequence: "90",
      timestamp: timestamp,
    };

    // Store detection
    this.state.detections.push(detection);

    // Keep only latest detections
    if (this.state.detections.length > this.config.maxDetections) {
      this.state.detections.shift();
    }

    console.log(
      "[stats collector] - [addDetection] - key sequence detected:",
      detection,
    );
  }

  /**
   * Add manual error report (can be called programmatically)
   */
  public reportError(timestamp: number = Date.now()): void {
    this.addDetection(timestamp);
    console.log("[stats collector] - [reportError] manual error reported");
  }

  /**
   * Get browser name from user agent
   */
  private getBrowserName(): string {
    const userAgent = navigator.userAgent;

    if (userAgent.includes("Chrome")) return "Chrome";
    if (userAgent.includes("Firefox")) return "Firefox";
    if (userAgent.includes("Safari") && !userAgent.includes("Chrome"))
      return "Safari";
    if (userAgent.includes("Edge")) return "Edge";
    if (userAgent.includes("Opera") || userAgent.includes("OPR"))
      return "Opera";

    return "Unknown";
  }

  /**
   * Get formatted timestamp (local time)
   * Format: YYYY_MM_DD-HH_mm_SS-SSS
   */
  private getFormattedTimestamp(): string {
    const now = new Date();

    const YYYY = now.getFullYear();
    const MM = String(now.getMonth() + 1).padStart(2, "0");
    const DD = String(now.getDate()).padStart(2, "0");
    const HH = String(now.getHours()).padStart(2, "0");
    const mm = String(now.getMinutes()).padStart(2, "0");
    const SS = String(now.getSeconds()).padStart(2, "0");
    const SSS = String(now.getMilliseconds()).padStart(3, "0");

    return `${YYYY}_${MM}_${DD}-${HH}_${mm}_${SS}-${SSS}`;
  }

  /**
   * Get formatted timestamp (GMT/UTC)
   * Format: YYYY_MM_DD-HH_mm_SS-SSS
   */
  private getGMTFormattedTimestamp(): string {
    const now = new Date();

    const YYYY = now.getUTCFullYear();
    const MM = String(now.getUTCMonth() + 1).padStart(2, "0");
    const DD = String(now.getUTCDate()).padStart(2, "0");
    const HH = String(now.getUTCHours()).padStart(2, "0");
    const mm = String(now.getUTCMinutes()).padStart(2, "0");
    const SS = String(now.getUTCSeconds()).padStart(2, "0");
    const SSS = String(now.getUTCMilliseconds()).padStart(3, "0");

    return `${YYYY}_${MM}_${DD}-${HH}_${mm}_${SS}-${SSS}`;
  }

  /**
   * Get current state
   */
  public getState(): Readonly<CollectorState> {
    return { ...this.state };
  }
}
export const eonStatsCollectorService = new EonStatsCollectorService();
